import { Tag, Tooltip } from 'antd';

import { getDataTypeMeta } from '../../utils/dictionary';

/**
 * THE data type chip: the type's glyph and its label, with the one-line
 * description as its tooltip. Wherever a data type is NAMED — the card
 * editor's collapsed element row, the display-template field menu, the
 * driven-from element list — this is the chip, so a type reads the same
 * everywhere and the dictionary is the only place its words live.
 *
 *   <DataTypeBadge dataType="measurement" />          sm (lists, rows)
 *   <DataTypeBadge dataType="date" size="md" />       md (headers)
 *   <DataTypeBadge dataType="" />                     "Choose a type"
 *
 * An unset or unknown type is not an error and not a blank: it renders the
 * dashed placeholder chip, which is exactly what a new element wears until
 * its author picks something (docs/plans/card-editor-dates-groups-filters.md
 * Part 1). The legacy `decimal` / `integer` spellings resolve to Number
 * through getDataTypeMeta rather than falling into the placeholder.
 *
 * `iconOnly` drops the label for the narrowest rows; the tooltip then names
 * the type, so nothing is lost.
 */
export default function DataTypeBadge({
	dataType,
	size = 'sm',
	iconOnly = false,
	className = '',
	placeholder = 'Choose a type',
}) {
	const meta = getDataTypeMeta(dataType);
	const classes = [
		'data-type-badge',
		`data-type-badge--${size}`,
		meta ? '' : 'data-type-badge--placeholder',
		iconOnly ? 'data-type-badge--icon-only' : '',
		className,
	].filter(Boolean).join(' ');

	if (!meta) {
		// An icon-only badge has no glyph to show for a type it does not
		// know, and the surfaces that use it (a template token, a driven-from
		// row) already name the element: showing "Choose a type" there would
		// be a demand nobody can act on. The placeholder is for the chip that
		// stands in for a type, which is where the answer is given.
		if (iconOnly) return null;
		return (
			<Tag className={classes} data-type="">
				{placeholder}
			</Tag>
		);
	}

	return (
		<Tooltip title={meta.description}>
			<Tag className={classes} data-type={meta.name}>
				{meta.bsIconJSX ? (
					<span className='data-type-badge__icon' aria-hidden='true'>{meta.bsIconJSX}</span>
				) : null}
				{iconOnly ? (
					<span className='visually-hidden'>{meta.label}</span>
				) : (
					<span className='data-type-badge__label'>{meta.label}</span>
				)}
			</Tag>
		</Tooltip>
	);
}
